<template>
    <div class="relative flex h-input w-full max-w-full flex-col">
        <label
            v-if="label"
            class="-ml-3 pb-4 text-xs"
        >
            {{ label }}
        </label>
        <Slider
            v-if="mounted"
            v-model="inputValue"
            class="my-auto"
            :step="step"
            :min="boundInputs ? parseInt(sliderBoxMin) : min"
            :max="boundInputs ? parseInt(sliderBoxMax) : max"
            :show-tooltip="showToolTipAlways ? 'always' : 'focus'"
            :classes="{
                target: 'relative box-border select-none touch-none tap-highlight-transparent touch-callout-none disabled:cursor-not-allowed',
                focused: 'slider-focused',
                tooltipFocus: 'slider-tooltip-focus',
                tooltipDrag: 'slider-tooltip-drag',
                ltr: 'slider-ltr',
                rtl: 'slider-rtl',
                horizontal: 'slider-horizontal h-1.5',
                vertical: 'slider-vertical w-1.5 h-80',
                textDirectionRtl: 'slider-txt-rtl',
                textDirectionLtr: 'slider-txt-ltr',
                base: 'w-full h-full relative z-1 bg-gray-300 rounded',
                connects: 'w-full h-full relative overflow-hidden z-0 rounded',
                connect: `absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full cursor-pointer tap:duration-300 tap:transition-transform disabled:bg-gray-400 disabled:cursor-not-allowed ${
                    errorMessage ? 'bg-error' : 'bg-brand'
                }`,
                origin: 'slider-origin absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full h:h-0 v:-top-full txt-rtl-h:left-0 txt-rtl-h:right-auto v:w-0 tap:duration-300 tap:transition-transform',
                handle: 'absolute rounded-full bg-white border-0 shadow-slider cursor-grab focus:outline-none h:w-8 h:h-8 h:top-[calc(50%+3px)] h:-translate-y-1/2 h:-right-2 txt-rtl-h:-left-2 txt-rtl-h:right-auto v:w-6 v:h-6 v:-top-2 v:-right-1.25 disabled:cursor-not-allowed focus:ring focus:ring-brand focus:ring-opacity-30',
                touchArea: 'h-full w-full',
                tooltip: `absolute block text-sm font-semibold whitespace-nowrap py-1 px-1.5 min-w-5 text-center text-white rounded border ${
                    errorMessage ? 'bg-error border-500' : 'bg-brand border-brand'
                } transform h:-translate-x-1/2 h:left-1/2 v:-translate-y-1/2 v:top-1/2 disabled:bg-gray-400 disabled:border-gray-400 merge-h:translate-x-1/2 merge-h:left-auto merge-v:-translate-x-4 merge-v:top-auto tt-focus:hidden tt-focused:block tt-drag:hidden tt-dragging:block`,
                tooltipTop: 'bottom-9 h:arrow-bottom merge-h:bottom-3.5',
                tooltipBottom: 'top-6 h:arrow-top merge-h:top-5',
                tooltipLeft: 'right-6 v:arrow-right merge-v:right-1',
                tooltipRight: 'left-6 v:arrow-left merge-v:left-7',
                tooltipHidden: 'slider-tooltip-hidden',
                active: 'slider-active shadow-slider-active cursor-grabbing',
                draggable: 'cursor-ew-resize v:cursor-ns-resize',
                tap: 'slider-state-tap',
                drag: 'slider-state-drag',
            }"
        ></Slider>
        <div
            class="pointer-events-none absolute -bottom-5 left-0 w-full truncate whitespace-nowrap text-xxs"
            :class="errorMessage ? 'text-error' : ''"
        >
            <span class="ml-2 text-xxs">{{ errorMessage }}</span>
        </div>
        <div
            v-if="boundInputs"
            class="absolute -bottom-10 flex w-full justify-between"
        >
            <MuvalInput
                v-model="sliderBoxMin"
                label="Min space"
                infoType="m³"
                class="w-36"
            />
            <MuvalInput
                v-model="sliderBoxMax"
                label="Max space"
                infoType="m³"
                class="w-36"
            />
        </div>
    </div>
</template>

<script>
import Slider from '@vueform/slider';
import MuvalInput from '@components/inputs/MuvalInput.vue';
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen.js';

export default {
    name: 'MuvalSlider',
    components: {
        Slider,
        MuvalInput,
    },
    props: {
        modelValue: {
            type: [Number, Array, null],
            required: false,
            default: null,
        },
        selectOptions: {
            type: [Array, null],
            required: false,
            default: null,
        },
        label: {
            type: [String, null],
            required: false,
            default: null,
        },
        min: {
            type: Number,
            required: false,
            default: 0,
        },
        max: {
            type: Number,
            required: false,
            default: 100,
        },
        vid: {
            type: String,
            required: false,
            default: () => idGen(6),
        },
        boundInputs: {
            type: Boolean,
            required: false,
            default: false,
        },
        showToolTipAlways: {
            type: Boolean,
            required: false,
            default: true,
        },
        step: {
            type: Number,
            required: false,
            default: 1,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const { value: inputValue, errorMessage } = useField(props.vid, undefined, {
            initialValue: props.modelValue ?? 0,
        });

        const sliderBox = ref({
            min: props.min,
            max: props.max,
        });
        const sliderBoxMin = computed({
            get: () => {
                return sliderBox.value.min;
            },
            set: (value) => {
                let num = parseInt(value);
                if (num >= 0 && num < sliderBox.value.max) {
                    sliderBox.value.min = parseInt(value);
                } else {
                    sliderBox.value.min = 0;
                }
            },
        });
        const sliderBoxMax = computed({
            get: () => {
                return sliderBox.value.max;
            },
            set: (value) => {
                let num = parseInt(value);
                if (num >= 0 && num > sliderBox.value.min) {
                    sliderBox.value.max = parseInt(value);
                } else {
                    sliderBox.value.max = sliderBox.value.min < 100 ? 100 : sliderBox.value.min + 1;
                }
            },
        });

        watch(
            inputValue,
            (newVal) => {
                console.log('Updating value slider', newVal, props.modelValue);
                if (Array.isArray(newVal)) {
                    if (newVal === props.modelValue) {
                        return;
                    }
                } else {
                    if (parseInt(newVal) === parseInt(props.modelValue)) {
                        return;
                    }
                }

                // sync the model value with vee-validate model
                if (Array.isArray(newVal)) {
                    emit('update:modelValue', newVal);
                } else {
                    emit('update:modelValue', parseInt(newVal));
                }
            },
            {
                immediate: false,
            },
        );

        // and you need to listen for `modelValue` prop changes
        watch(
            () => props.modelValue,
            (newModel) => {
                if (Array.isArray(newModel)) {
                    if (newModel === inputValue.value) {
                        return;
                    }
                } else {
                    if (parseInt(newModel) === parseInt(inputValue.value)) {
                        return;
                    }
                }

                // Sync the vee-validate model with model value
                if (Array.isArray(newModel)) {
                    inputValue.value = newModel;
                } else {
                    inputValue.value = parseInt(newModel);
                }
            },
        );

        const mounted = ref(false);
        onMounted(async () => {
            if (props.modelValue) {
                if (Array.isArray(props.modelValue)) {
                    inputValue.value = props.modelValue;
                } else {
                    inputValue.value = parseInt(props.modelValue);
                }
            }
            mounted.value = true;
        });

        return {
            mounted,
            errorMessage,
            inputValue,
            sliderBoxMin,
            sliderBoxMax,
        };
    },
};
</script>
